import { SortDescriptor } from "@progress/kendo-data-query";
import { IBaseSearchRequest } from "../../core/models/base";
import { ExpenseTypeKind, IExpenseType } from "./expense-type";

// Permission that lets a user decide on their own expenses (e.g. an owner without a supervisor).
export const EXPENSES_APPROVE_SELF_PERMISSION = 'Permissions.Expenses.ApproveSelf';

// Permission for the accounting step (pay out / reject accepted expenses across all employees).
export const EXPENSES_ACCOUNTING_PERMISSION = 'Permissions.Expenses.Accounting';

// Basic permission of the expense module. It is absent entirely when the tenant has no expense
// module, so it gates every expense request the app fires on its own (e.g. the review badge).
export const EXPENSES_SEARCH_PERMISSION = 'Permissions.Expenses.Search';

// Numeric enum to match the backend (System.Text.Json serializes enums as numbers).
export enum ExpenseStatus {
  Open = 0,
  Submitted = 10,
  Accepted = 20,
  Rejected = 30,
  Paid = 40
}

export interface IExpense {
  id: string;
  description?: string;
  expenseTypeId: string;
  amount: number;
  km?: number | null;
  linkedAssignmentId?: string | null;
  submitterId: string;
  status: ExpenseStatus;
  // Expense date chosen by the user (defaults to today on creation). Distinct from createdOn.
  date?: string;
  createdOn?: string;
  // flattened display fields from the backend
  submitterFirstName?: string;
  submitterLastName?: string;
  expenseTypeName?: string;
  // Salary-payment flag of the expense type (Lohnzahlung) — shown in the accounting list.
  expenseTypeSalaryPayment?: boolean;
}

export interface IExpenseHistory {
  id: string;
  linkedExpenseId: string;
  status: ExpenseStatus;
  decisionDate?: string | null;
  decisionMakerId?: string | null;
  rejectionReason?: string | null;
  createdOn: string;
}

export interface IExpenseDetails extends IExpense {
  expenseType?: IExpenseType;
  expenseHistory?: IExpenseHistory[];
  linkedAssignmentTitle?: string | null;
}

export interface ICreateExpenseRequest {
  expenseTypeId: string;
  description?: string;
  km?: number | null;
  amount?: number | null;
  linkedAssignmentId?: string | null;
  // Date-only string (yyyy-MM-dd) so the calendar day is preserved regardless of timezone.
  date?: string | null;
}

export interface IUpdateExpenseRequest extends ICreateExpenseRequest {
  id: string;
}

export interface IRejectExpenseRequest {
  id: string;
  rejectionReason: string;
}

export interface ISearchExpensesRequest extends IBaseSearchRequest {
  forApproval?: boolean;
  statuses?: ExpenseStatus[];
  includeOwn?: boolean;
}

export const EXPENSES_DEFAULT_ORDER_BY = ['date desc'];

// Maps the grid's sort descriptor to the backend orderBy. The submitter column sorts on the
// Submitter navigation (last, then first name) with the date as a stable tie-breaker; the backend
// resolves dotted paths and is case-insensitive on property names.
export function expenseSortToOrderBy(sort: SortDescriptor[]): string[] {
  const first = sort?.[0];
  if (!first?.field || !first.dir) {
    return EXPENSES_DEFAULT_ORDER_BY;
  }
  if (first.field === 'submitterLastName') {
    return [`submitter.lastName ${first.dir}`, `submitter.firstName ${first.dir}`, 'date desc'];
  }
  return [`${first.field} ${first.dir}`];
}

// Numeric enum to match the backend (System.Text.Json serializes enums as numbers).
export enum ExportFormat {
  Excel = 0,
  Pdf = 10
}

export interface IExportExpensesRequest extends ISearchExpensesRequest {
  // When set, only these expenses are exported; otherwise the whole filtered set is exported.
  selectedExpenseIds?: string[];
  format?: ExportFormat;
}

export { ExpenseTypeKind };
